import { Plug } from "@lucasmarkes/hairline/react";
import { type ErrorComponentProps, useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import { isServerUnreachableError } from "../environments/primary/bootstrapRequest";
import { AppErrorScreen } from "./AppErrorScreen";
import { ReportedErrorScreen } from "./ReportedErrorScreen";

/**
 * Delay before an unreachable server is tried again. The session read itself
 * retries for 15 s before giving up, so a restart is retried about every 20 s.
 */
export const SERVER_UNREACHABLE_RETRY_MS = 5_000;

/**
 * TanStack's error boundary for the whole route tree. React 19 has already
 * handed the error to Sentry through the root's error handlers (see
 * sentry.ts), so this only shows the screen and offers a way back.
 */
export function RootRouteErrorView({ error }: ErrorComponentProps) {
  const router = useRouter();
  // Invalidation replaces the match, which resets TanStack's error boundary.
  const retry = () => void router.invalidate();
  if (isServerUnreachableError(error)) return <ServerUnreachableScreen error={error} onRetry={retry} />;
  return <ReportedErrorScreen onRetry={retry} />;
}

/**
 * The server is restarting (a deploy) or down. Nothing was reported, and the
 * screen says so; it retries on its own, and each failed retry hands this a
 * new error, which schedules the next attempt.
 */
function ServerUnreachableScreen({
  error,
  onRetry,
}: {
  readonly error: unknown;
  readonly onRetry: () => void;
}) {
  const router = useRouter();
  useEffect(() => {
    const timer = setTimeout(() => void router.invalidate(), SERVER_UNREACHABLE_RETRY_MS);
    return () => clearTimeout(timer);
  }, [router, error]);
  return (
    <AppErrorScreen
      notified={false}
      art={Plug}
      title="Can't reach Amoreg right now"
      message="The server isn't responding. It may be restarting after an update. Retrying automatically."
      onRetry={onRetry}
    />
  );
}
